/**
 * Convierte la forma habitual de `account.authorities` (array de strings, de
 * objetos { name | authority | role }, string CSV o JSON array) en lista de
 * strings. Evita usar `.includes` sobre un string completo (subcadena).
 */
export function normalizeAuthorityStrings(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item): string => {
        if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
        if (item && typeof item === 'object') {
          const o = item as Record<string, unknown>;
          const v = o.name ?? o.authority ?? o.role;
          if (typeof v === 'string') return v.trim();
        }
        return '';
      })
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        return normalizeAuthorityStrings(JSON.parse(t));
      } catch {
        /* seguir como CSV */
      }
    }
    return t.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (typeof raw === 'object' && raw !== null && 'strings' in raw && Array.isArray((raw as { strings: unknown }).strings)) {
    return normalizeAuthorityStrings((raw as { strings: unknown }).strings);
  }
  return [];
}

/**
 * Determina si el usuario posee alguna de las autoridades indicadas.
 * Si `requiredAuthorities` está vacío, se permite a cualquier usuario que tenga
 * al menos una autoridad en `authorities` (misma semántica que el menú lateral).
 */
export function hasAnyAuthority(authorities: unknown, requiredAuthorities: string[]): boolean {
  const list = normalizeAuthorityStrings(authorities);
  if (list.length !== 0) {
    if (requiredAuthorities.length === 0) return true;
    return requiredAuthorities.some(auth => list.includes(auth));
  }
  return false;
}

export const AUTHORITY_APLICACION_ACCEDER = 'APLICACION_ACCEDER_A_LA_APLICACION';

/** Acceso al dashboard (/) y elementos de resumen del CRM. */
export const APLICACION_ACCEDER_REQUIRED_AUTHORITIES: string[] = [
  AUTHORITY_APLICACION_ACCEDER,
  `ROLE_${AUTHORITY_APLICACION_ACCEDER}`,
];

export function canAccessDashboard(authorities: unknown): boolean {
  return hasAnyAuthority(authorities, APLICACION_ACCEDER_REQUIRED_AUTHORITIES);
}

/** Permiso de UI/API para `/configuracion/usuarios/**` y `/configuracion/seguridad/**` (Spring puede mandar también con prefijo ROLE_). */
export const AUTHORITY_USUARIOS_CONFIGURACION = 'USUARIOS_CONFIGURACION_DE_USUARIOS_Y_PERMISOS';

export const USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES: string[] = [
  AUTHORITY_USUARIOS_CONFIGURACION,
  `ROLE_${AUTHORITY_USUARIOS_CONFIGURACION}`,
];

/** Acceso a `/configuracion/usuarios/**` y `/configuracion/seguridad/**`. */
export function canAccessUsuariosConfiguracion(authorities: unknown): boolean {
  return hasAnyAuthority(authorities, USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES);
}

/** UI para `#/empresas` (Spring puede enviar también `ROLE_…`). */
export const AUTHORITY_BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS = 'BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS';

export const BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES: string[] = [
  AUTHORITY_BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS,
  `ROLE_${AUTHORITY_BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS}`,
];

/** Editar datos de empresas / grupos en UI (lápiz en cabecera de grupo, etc.). */
export const AUTHORITY_BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS = 'BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS';

export const BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES: string[] = [
  AUTHORITY_BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS,
  `ROLE_${AUTHORITY_BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS}`,
];

export function canAccessEmpresas(authorities: unknown): boolean {
  return hasAnyAuthority(authorities, BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES);
}
